import { Images } from '@/assets/images';
import { AVATAR_ACCEPT, REGEX } from '@/constant';
import { useBlobFile } from '@/hooks';
import {
	cn,
	compressImage,
	convertAppleFileToJpgFile,
	getBase64,
	isFileTypeInvalid,
	isHeicHeifFile,
	showAlert,
	validateAllowFile,
} from '@/utils';
import {
	InputHTMLAttributes,
	memo,
	useCallback,
	useRef,
	useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import Loading from '../loading';

export type FileInputData = {
	name: string;
	imgData: string;
};

export type InputFileFormProps = Omit<
	InputHTMLAttributes<HTMLInputElement>,
	'onChange'
> & {
	className?: string;
	classNameIconDelete?: string;
	dataImg?: string;
	onChange?: (data: FileInputData) => void;
};

const InputFileForm = ({
	className,
	dataImg,
	onChange,
	...props
}: InputFileFormProps) => {
	const { t } = useTranslation('validation');

	const { filePath } = useBlobFile(dataImg || '', 'blob');

	const inputRef = useRef<HTMLInputElement>(null);

	const [isLoading, setIsLoading] = useState(false);

	const onClickFile = useCallback(() => {
		inputRef?.current?.click();
	}, []);

	const onChangeFile = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			try {
				setIsLoading(true);
				let file = e.target.files?.[0] as File;
				const name = e.target.name;

				if (props.accept && isFileTypeInvalid(props.accept, file?.name || '')) {
					return showAlert(t('ALERT_6'));
				}

				if (
					!validateAllowFile(
						file,
						REGEX.ALLOW_IMAGES,
						t('ALERT_6'),
						true,
						t('ALERT_6')
					)
				)
					return;

				if (isHeicHeifFile(file?.name)) {
					file = await convertAppleFileToJpgFile(file);
				}

				const newImageResized = await compressImage(file);

				const imgData = (await getBase64(newImageResized || file)) as string;

				onChange?.({
					imgData,
					name,
				});
			} catch (error) {
				console.error('Cannot convert avatar img to base64 :' + error);
			} finally {
				// Reset input value to allow re-uploading the same file
				if (inputRef.current) {
					inputRef.current.value = '';
				}
				setIsLoading(false);
			}
		},
		[onChange]
	);

	const handleDeleteImg = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			e.stopPropagation();
			onChange?.({
				imgData: '',
				name: props.name || '',
			});
			// Reset input value
			if (inputRef.current) {
				inputRef.current.value = '';
			}
		},

		[onChange, props.name, inputRef]
	);

	return (
		<div
			className={cn(
				'cursor-pointer relative',
				className,
				filePath && 'bg-white',
				isLoading && 'cursor-default'
			)}
			onClick={() => !isLoading && onClickFile()}
		>
			<Loading className="m-0 w-full h-full" isLoading={isLoading} />
			<input
				type="file"
				className="hidden"
				ref={inputRef}
				accept={AVATAR_ACCEPT}
				onChange={onChangeFile}
				onClick={(e) => {
					e.stopPropagation();
				}}
				{...props}
			/>
			{filePath && (
				<>
					<img
						src={filePath}
						alt="input file"
						className="w-full h-full object-contain"
					/>
					<div
						className={cn(
							'absolute top-0 right-0 w-6 h-6 icon-delete-img-form',
							props?.classNameIconDelete
						)}
						onClick={handleDeleteImg}
					>
						<img src={Images.iconDeleteImg} alt="delete" />
					</div>
				</>
			)}
		</div>
	);
};

export default memo(InputFileForm);
